import React, {useEffect, useMemo} from "react";
import Heading from "../../../components/Heading";
import IconButton from "../../../components/IconButton";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {ButtonSetting} from "../../../types/ButtonSetting";
import ColorPicker from "../../../components/ColorPicker";
import Input from "../../../components/Input";
import Select from "../../../components/Select";
import Button from "../../../components/Button";
import IndividualizationButton from "./IndividualizationButton";
import {useGlobalContext} from "../../../contexts/globalContext";
import Loader from "../../../components/Loader";
import {useFetchSingle} from "../../../hooks/useFetchSingle";
import * as yup from "yup"
import {useStoreData} from "../../../hooks/useStoreData";
import {useTranslation} from "react-i18next";
import cx from "classnames";
import styles from "./ButtonSetting.module.css";
const ButtonSettings: React.FC<{changeSettingsTab: (tab: string|null) => void}> = ({changeSettingsTab}) => {
    const {data, isLoading, fetch} = useFetchSingle<ButtonSetting>('buttonSettings')
    const {plugin, showToast} = useGlobalContext()
    const { t } = useTranslation(['settings'])
    const options = useMemo(() => {
        return [
            { value: "html", label: t('button.options.html')},
            { value: "after", label: t('button.options.after')},
            { value: "append", label: t('button.options.append')},
            { value: "before", label: t('button.options.before')},
            { value: "prepend", label: t('button.options.prepend')}
        ]
    }, [t])

    const updateButtonSettingsSchema = useMemo(() => {
        return ({
            selector: yup.string().required(),
            position: yup.string().oneOf(options.map((o) => o.value)),
            text: yup.string().required().min(3),
            classes: yup.string(),
            text_color: yup.string().required(),
            background_color: yup.string().required()
        })
    }, [options])

    const {
        element: buttonSettings,
        isValid,
        submit,
        changeProp,
    } = useStoreData<ButtonSetting>('buttonSettings', updateButtonSettingsSchema)

    useEffect(() => {
        fetch()
    }, [fetch])

    useEffect(() => {
        if (data) changeProp(data)
    }, [data, changeProp])

    const handleSubmit = () => {
        submit().then((res) => {
            if (res.data?.message) {
                showToast('top-right', 'success', t('button.success'))
            } else {
                showToast('top-right', 'error', t('button.error'))
            }
        })
    }

    return (
        <>
            <Heading level={3} classes={'my-4'}>
                {t('button.heading')}
            </Heading>
            <IconButton
                icon={faArrowLeft}
                color={'black'} onClick={() => changeSettingsTab(null)}
            >
                {t('common.backToOverview')}
            </IconButton>
            {!isLoading && <div>
                <div  className={cx(styles['button-list'])}>
                    <div className={'col'}>
                        <Heading level={4} classes={'mt-4 mb-2'}>
                            {t('button.sub_heading')}
                        </Heading>
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.selector')}
                            property={'selector'}
                            value={buttonSettings.selector ?? ''}
                            onChange={(property, value) => changeProp({selector: value})}
                            classes={'mt-3 mb-6'}
                        />
                        <Select
                            placeholder={t('button.labels.position')}
                            searchable={false}
                            options={options}
                            property={'position'}
                            value={buttonSettings?.position ?? ''}
                            onSelect={(property, value) => changeProp({position: value})}
                            classes={'my-6'}
                        />
                        <Heading level={4} classes={'mt-4 mb-2'}>
                            Design
                        </Heading>
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.text')}
                            property={'text'}
                            value={buttonSettings?.text ?? ''}
                            onChange={(property, value) => changeProp({text: value})}
                            classes={'mt-3 mb-6'}
                            multiLanguage={true}
                            multiLanguageValue={buttonSettings.text_multi}
                            onMultiLanguageChange={(property, value) => changeProp({text_multi: value})}
                        />
                        <Input
                            type={'text'}
                            placeholder={t('button.labels.css')}
                            property={'classes'}
                            value={buttonSettings?.classes ?? ''}
                            onChange={(property, value) => changeProp({classes: value})}
                            classes={'my-6'}
                        />
                        <div className={'row'}>
                            <div className={'col mb-4'}>
                                <ColorPicker
                                    classes={'ml-4' }
                                    property={'text_color'}
                                    value={buttonSettings?.text_color ?? ''}
                                    onChange={(property, value) => changeProp({text_color: value})}
                                >
                                    {t('button.labels.text_color')}
                                </ColorPicker>
                            </div>
                            <div className={'col mb-4'}>
                                <ColorPicker
                                    classes={'ml-4' }
                                    property={'text_color'}
                                    value={buttonSettings?.icon_color ?? ''}
                                    onChange={(property, value) => changeProp({icon_color: value})}
                                >
                                    {t('button.labels.icon_color')}
                                </ColorPicker>
                            </div>
                            <div className={'col'}>
                                <ColorPicker
                                    classes={'ml-4'}
                                    property={'background_color'}
                                    value={buttonSettings?.background_color ?? ''}
                                    onChange={(property, value) => changeProp({background_color: value})}
                                >
                                    {t('button.labels.background_color')}
                                </ColorPicker>
                            </div>
                        </div>
                    </div>
                    <div className={'col'}>
                        <Heading level={4} classes={'mt-4 mb-2'}>
                            {t('button.preview')}
                        </Heading>
                        {(buttonSettings && data) &&
                            <IndividualizationButton
                                text={buttonSettings.text ?? data.text}
                                background_color={buttonSettings.background_color ?? data.background_color}
                                text_color={buttonSettings.text_color ?? data.text_color}
                                icon_color={buttonSettings.icon_color ?? data.icon_color}
                                classes={buttonSettings.classes ?? data.classes}
                            />
                        }
                    </div>
                </div>
                <div className={'w-100 d-flex my-6'}>
                    <Button
                        hierarchy={'primary'}
                        classes={'w-50'} text={t('button.action')}
                        fn={() => handleSubmit()}
                        disabled={!isValid}
                    />
                </div>
            </div>}
            {isLoading &&
                <div className={'d-flex w-100 justify-content-center'}>
                    <Loader isLoading={isLoading} size={'m'}/>
                </div>
            }
        </>
    )
}

export default ButtonSettings
